import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ValidationResult } from "@lchemy/model/validation";
import { filter, finalize, first, startWith, takeUntil } from "rxjs/operators";

import { FORM_ERROR_FORMATTER_MAP } from "@lantern/app/common";
import { ModalTemplateDirective } from "@lantern/app/common/utils/modals";
import { AuthJwtService, GrowlService } from "@lantern/app/core";

import { GlobalAuthApi } from "@local/app/core/api/global-auth";
import { User, updateUserInfoCmdValidator } from "@local/app/models";
import { LOGIN_ERROR_MAP } from "@local/app/pages/shared";

@Component({
  selector: "ta-lcl-user-settings",
  templateUrl: "user-settings.template.html",
  providers: [
    {
      provide: FORM_ERROR_FORMATTER_MAP,
      useValue: LOGIN_ERROR_MAP
    }
  ]
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  user: User | undefined;
  private token: string | undefined;

  @ViewChild("modal", { static: false })
  currentPasswordModal: ModalTemplateDirective<string>;

  userForm = this.formBuilder.group({
    name: "",
    password: "",
    passwordConfirm: "",
    existingPassword: ""
  });
  userFormValidator = updateUserInfoCmdValidator;
  validationResult: ValidationResult;
  forceShowErrors: boolean = false;

  changePassword = false;

  constructor(
    private authJwt: AuthJwtService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private globalAuthApi: GlobalAuthApi,
    private growlService: GrowlService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.authJwt.globalJwtToken
      .pipe(
        filter((token) => token != null),
        first(),
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe((token) => (this.token = token));

    const user = this.activatedRoute.snapshot.data.user as User;
    if (user == null) {
      this.router.navigateByUrl("/");
      return;
    }
    this.user = user;
    this.userForm.patchValue({ name: user.name });

    this.initValidation();
  }

  private onDestroyEmitter = new EventEmitter();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  saving = false;
  async save(): Promise<void> {
    await this.validate();
    if (!this.validationResult.isValid) {
      this.growlService.open("formError");
      return;
    }

    const { name: newName } = this.userForm.value,
      isUpdatingName =
        newName != null && newName.length > 0 && newName !== this.user!.name;
    if (!isUpdatingName) {
      this.userForm.patchValue({ name: this.user!.name });
    }

    const { password: newPassword } = this.userForm.value;
    const isUpdatingPassword = newPassword != null && newPassword.length > 0;
    if (isUpdatingPassword && this.user!.usesPassword) {
      try {
        const existingPassword = await this.currentPasswordModal
          .open()
          .response.toPromise();
        this.userForm.patchValue({ existingPassword });
      } catch {
        this.growlService.warning("Canceled save");
      }
    }

    if (!isUpdatingName && !isUpdatingPassword) {
      this.resetForm();
      this.growlService.open("saveSucceeded");
      return;
    }

    const updateUserCmd = isUpdatingPassword
      ? this.userForm.value
      : { name: this.userForm.value.name };

    this.saving = true;
    this.globalAuthApi
      .updateUserInfo(updateUserCmd, this.user!.email, this.token!)
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe(
        () => {
          if (this.userForm.value.name != null) {
            this.user!.name = this.userForm.value.name;
          }
          if (this.userForm.value.password != null) {
            this.user!.usesPassword = true;
          }
          this.resetForm();
          this.growlService.open("saveSucceeded");
        },
        () => this.growlService.open("saveFailed")
      );
  }

  private resetForm(): void {
    this.changePassword = false;

    this.userForm.setValue({
      name: this.user!.name,
      password: "",
      passwordConfirm: "",
      existingPassword: ""
    });
  }

  private initValidation(): void {
    this.userForm.valueChanges
      .pipe(startWith(true), takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.validate());
  }

  async validate(): Promise<void> {
    this.validationResult = await this.userFormValidator.validate(
      this.userForm.value
    );
  }
}
